import React, {
  useEffect,
  useState,
} from 'react';
import { ModelsTableHead, } from './ModelsTableHead';
import { ModelsTableToolbar, } from './ModelsTableToolbar';
import {
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  Edit,
} from '@mui/icons-material';
import { Interfaces, } from '../../../config';
import { collectionServices, } from '../../../services';
import { useNavigate, } from 'react-router-dom';
import { useSnackbar, } from 'notistack';
import { helpers, } from '../../../utils';
import { useAuth, } from '../../../context';
import ModalConfirmation from '../../ModalConfirmation/ModalConfirmation';
import { makeStyles, } from '@mui/styles';
import { DataSourceLogo, } from '../../DatasourceIcon';

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: '10px',
  },
  table: {
    minWidth: 750,
  },
  dataSourceActionsCell: {
    minWidth:'154px',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
});

const ModelsTable = ({
  models,
  updateModels,
}: {
  models: Array<Interfaces.Model>;
  // eslint-disable-next-line no-unused-vars
  updateModels: (models: Array<Interfaces.Model>) => void;
}) => {
  const classes = useStyles();
  const history = useNavigate();
  const [filteredModels, setFilteredModels,] = useState<Array<Interfaces.Model>>([]);
  const [modelToDelete, setModelToDelete,] = useState<Interfaces.Model | null>(null);
  const [deletePopupState, setDeletePopupState,] = useState(false);
  const [filters, setFilters,] = useState({
    checked: false,
    order: 'asc',
    orderBy: 'connectionName',
    page: 0,
    rowsPerPage: 5,
  });
  const { enqueueSnackbar, } = useSnackbar();

  const { isAuthorized, deploymentType, } = useAuth();
  const [writeAccess,] = React.useState(isAuthorized('ADAPTIVE_COL_WRITE'));
  const [readAccess,] = React.useState(isAuthorized('ADAPTIVE_COL_READ'));
  const [readDSAccess,] = React.useState(isAuthorized('ADAPTIVE_DS_READ'));
  const [readDepAccess,] = React.useState(isAuthorized('ADAPTIVE_DEP_READ'));
  const [adminAccess,] = React.useState(isAuthorized('ADAPTIVE_ADMIN'));
  const [executeAccess,] = React.useState(isAuthorized('ADAPTIVE_COL_EXECUTE'));

  useEffect(() => {
    setFilteredModels(models);
  }, []);

  const descendingComparator = (a: any, b: any, orderBy: any) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order: any, orderBy: any) => {
    return order === 'desc'
      ? (a: any, b: any) => descendingComparator(a, b, orderBy)
      : (a: any, b: any) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (dataSources: any, comparator: any) => {
    const stabilizedThis = dataSources.map((el: any, index: number) => [
      el,
      index,
    ]);
    stabilizedThis.sort((a: any, b: any) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el: any) => el[0]);
  };

  const handleSearch = (event: any) => {
    let filteredData = models.filter((value) =>
      value.name.toLowerCase().includes(event.target.value.toLowerCase())
    );
    setFilteredModels(filteredData);
  };

  const handleRequestSort = (event: any, property: any) => {
    const isAsc = filters.orderBy === property && filters.order === 'asc';
    setFilters({
      ...filters,
      order: isAsc ? 'desc' : 'asc',
      orderBy: property,
    });
  };

  const handleChangePage = (event: any, newPage: any) => {
    setFilters({
      ...filters,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (event: any) => {
    setFilters({
      ...filters,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
    });
  };


  const handleEdit = (model: Interfaces.Model) => {
    history(`/models/edit/${model.id}`);
  };

  const handleDeletePopup = (model: Interfaces.Model | null) => {
    setDeletePopupState(!deletePopupState);
    setModelToDelete(model);
  };

  const deleteAction = () => {
    if (modelToDelete && modelToDelete.id) {
      collectionServices.deleteCollection(modelToDelete.id)
        .then(() => {
          setDeletePopupState(false);
          enqueueSnackbar('delete success', { variant: 'success', });
          updateModels(models.filter((_model) => _model.id !== modelToDelete.id));
          setFilteredModels(models.filter((_model) => _model.id !== modelToDelete.id));
        })
        .catch((error) => {
          enqueueSnackbar(helpers.getErrorMessage(error), { variant: 'error', });
        });
    }
  };

  return (
    <div className={classes.root}>
      <Paper hidden={!readAccess} className={classes.paper}>
        <ModelsTableToolbar handleSearch={handleSearch}/>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={'medium'}
            aria-label="enhanced table"
          >
            <ModelsTableHead
              classes={classes}
              order={filters.order}
              orderBy={filters.orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {
                stableSort(
                  filteredModels,
                  getComparator(filters.order, filters.orderBy)
                )
                  .slice(filters.page * filters.rowsPerPage, filters.page * filters.rowsPerPage + filters.rowsPerPage)
                  .map(
                    (model: Interfaces.Model, index: number) => {
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={model.name}
                        >
                          <TableCell>{index + 1}</TableCell>
                          <TableCell align="left">{model.name}</TableCell>
                          <TableCell align="left" >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                            }}>
                              <DataSourceLogo
                                name={model.database.databaseType as any}
                                type={model.database.databaseType as any}
                                margin={'0 20px'}
                                maxWidth={'50px'}
                                maxHeight={'30px'}
                              />
                              {model.dataSourceId !== '-1' ?
                                `${model.database.databaseProductName} / ${model.database.databaseType.toUpperCase()}` :
                                `New DataSource / ${model.database.databaseType.toUpperCase()}`}
                            </div>

                          </TableCell>
                          <TableCell align="left" className={classes.dataSourceActionsCell}>
                            {
                              readDSAccess && readAccess && writeAccess && (
                                <>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      style={{
                                        padding: '3px 3px',
                                        marginRight: '10px',
                                      }}
                                      aria-label="edit"
                                      onClick={() => {
                                        handleEdit(model);
                                      }}
                                    >
                                      <Edit/>
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )
                            }
                            {
                              (adminAccess || deploymentType === 'cloud') && (
                                <>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      style={{
                                        padding: '3px 3px',
                                        marginRight: '10px',
                                      }}
                                      aria-label="delete"
                                      onClick={() => {
                                        handleDeletePopup(model);
                                      }}
                                    >
                                      <Delete/>
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )
                            }
                            {
                              executeAccess && readDepAccess && (
                                <Chip
                                  variant="outlined"
                                  size="small"
                                  label="Deploy"
                                  style={{
                                    fontWeight: 'bold',
                                    color: 'white',
                                    background: 'green',
                                    borderColor: 'white',
                                  }}
                                  onClick={() => history(`/models/${model.id}/deployment`)}
                                />
                              )
                            }
                          </TableCell>

                        </TableRow>
                      );
                    }
                  )
              }
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25,]}
          component="div"
          count={filteredModels.length}
          rowsPerPage={filters.rowsPerPage}
          page={filters.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <ModalConfirmation
        open={deletePopupState}
        onClose={() => handleDeletePopup(null)}
        title={'Are you sure you want to delete the Git Config?'}
        onBtnClick={() => deleteAction()}
        colorBtn={'secondary'}
        btnText={'Delete'}
        onCancelBtnClick={() => handleDeletePopup(null)} />
    </div>
  );
};

export { ModelsTable, };
